// Admin Service - GraphQL operations for Admin role
import { graphqlClient } from "../graphqlClient";

// ==================== Engine Operations ====================

export const getAllEngines = async () => {
  const query = `
    query GetAllEngines {
      getAllEngines {
        id
        code
        email
        name
        status
        resellerCount
        merchantCount
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.query(query);
  return result.getAllEngines;
};

export const getEngineById = async (id) => {
  const query = `
    query GetEngineById($id: String!) {
      getEngineById(id: $id) {
        id
        code
        email
        name
        status
        depositCommission
        withdrawalCommission
        percentageFee
        allowedProviders
        maxResellers
        maxMerchants
        contactName
        contactPhone
        supportEmail
        notes
        apiKey
        secretKey
        withdrawalFee
        permissions {
          can_create_merchants
          can_create_resellers
          can_edit_fees
          can_regenerate_api_keys
          can_view_webhook_logs
        }
        depositFee
        resellerCount
        merchantCount
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.query(query, { id });
  return result.getEngineById;
};

export const getMyCreatedEngines = async () => {
  const query = `
    query GetMyCreatedEngines {
      getMyCreatedEngines {
        id
        code
        email
        name
        status
        resellerCount
        merchantCount
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.query(query);
  return result.getMyCreatedEngines;
};

export const createEngine = async (input) => {
  const mutation = `
    mutation CreateEngine($input: CreateEngineInput!) {
      createEngine(input: $input) {
        id
        code
        email
        name
        status
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.mutate(mutation, { input });
  return result.createEngine;
};

export const updateEngine = async (id, input) => {
  const mutation = `
    mutation UpdateEngine($id: String!, $input: UpdateEngineInput!) {
      updateEngine(id: $id, input: $input) {
        id
        code
        email
        name
        status
        depositCommission
        withdrawalCommission
        percentageFee
        allowedProviders
        maxResellers
        maxMerchants
        contactName
        contactPhone
        supportEmail
        notes
        apiKey
        secretKey
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.mutate(mutation, { id, input });
  return result.updateEngine;
};

export const deleteEngine = async (id) => {
  const mutation = `
    mutation DeleteEngine($id: String!) {
      deleteEngine(id: $id)
    }
  `;

  const result = await graphqlClient.mutate(mutation, { id });
  return result.deleteEngine;
};

export const changeEnginePassword = async (id, newPassword) => {
  const mutation = `
    mutation ChangeEnginePassword($input: ChangePasswordInput!) {
      changeEnginePassword(input: $input)
    }
  `;

  const result = await graphqlClient.mutate(mutation, {
    input: { id, newPassword },
  });
  return result.changeEnginePassword;
};

// ==================== Reseller Operations ====================

export const getAllResellers = async () => {
  const query = `
    query GetAllResellers {
      getAllResellers {
        id
        code
        email
        name
        status
        createdByAdminId
        createdByEngineId
        merchantCount
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.query(query);
  return result.getAllResellers;
};

export const getResellerById = async (id) => {
  const query = `
    query GetResellerById($id: String!) {
      getResellerById(id: $id) {
        id
        code
        email
        name
        status
        contactName
        contactPhone
        supportEmail
        notes
        depositCommission
        withdrawalCommission
        percentageFee
        depositFee
        withdrawalFee
        commissionRate
        maxMerchants
        merchantCount
        allowedProviders
        routingOverride
        permissions
        apiKey
        secretKey
        createdByAdminId
        createdByEngineId
        parentName
        parentType
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.query(query, { id });
  return result.getResellerById;
};

export const getMyCreatedResellers = async () => {
  const query = `
    query GetMyCreatedResellers {
      getMyCreatedResellers {
        id
        code
        email
        name
        status
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.query(query);
  return result.getMyCreatedResellers;
};

export const createReseller = async (input) => {
  const mutation = `
    mutation CreateReseller($input: CreateResellerInput!) {
      createReseller(input: $input) {
        id
        code
        email
        name
        status
        contactName
        contactPhone
        supportEmail
        notes
        commissionRate
        depositFee
        withdrawalFee
        percentageFee
        allowedProviders
        routingOverride
        permissions
        maxMerchants
        apiKey
        secretKey
        createdByAdminId
        createdByEngineId
        parentType
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.mutate(mutation, { input });
  return result.createReseller;
};

export const updateReseller = async (id, input) => {
  const mutation = `
    mutation UpdateReseller($id: String!, $input: UpdateResellerInput!) {
      updateReseller(id: $id, input: $input) {
        id
        code
        email
        name
        status
        contactName
        contactPhone
        supportEmail
        notes
        commissionRate
        depositFee
        withdrawalFee
        percentageFee
        allowedProviders
        routingOverride
        permissions
        maxMerchants
        apiKey
        secretKey
        createdByAdminId
        createdByEngineId
        parentType
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.mutate(mutation, { id, input });
  return result.updateReseller;
};

export const deleteReseller = async (id) => {
  const mutation = `
    mutation DeleteReseller($id: String!) {
      deleteReseller(id: $id)
    }
  `;

  const result = await graphqlClient.mutate(mutation, { id });
  return result.deleteReseller;
};

export const changeResellerPassword = async (id, newPassword) => {
  const mutation = `
    mutation ChangeResellerPassword($input: ChangePasswordInput!) {
      changeResellerPassword(input: $input)
    }
  `;

  const result = await graphqlClient.mutate(mutation, {
    input: { id, newPassword },
  });
  return result.changeResellerPassword;
};

// ==================== Merchant Operations ====================

export const getAllMerchants = async () => {
  const query = `
    query GetAllMerchants {
      getAllMerchants {
        id
        code
        email
        name
        status
        depositCommission
        withdrawalCommission
        percentageFee
        fee_type
        fee_rate
        settlementPeriod
        depositEnabled
        withdrawalEnabled
        currency
        minDeposit
        maxDeposit
        minWithdrawal
        maxWithdrawal
        dailyDepositLimit
        dailyWithdrawalLimit
        notes
        callbackUrl
        webhookUrl
        ipWhitelistEntries {
          ip
          statusIp
        }
        createdByAdminId
        createdByEngineId
        createdByResellerId
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.query(query);
  return result.getAllMerchants || [];
};

export const getMerchantsByEngine = async (engineId) => {
  const query = `
    query GetMerchantsByEngine($engineId: ID!) {
      getMerchantsByEngine(engineId: $engineId) {
        id
        code
        email
        name
        status
     
        fee_type
        fee_rate
        settlementPeriod
        depositEnabled
        withdrawalEnabled
        currency
        minDeposit
        maxDeposit
        minWithdrawal
        maxWithdrawal
        dailyDepositLimit
        dailyWithdrawalLimit
        notes
        callbackUrl
        webhookUrl
        ipWhitelistEntries {
          ip
          statusIp
        }
        createdByAdminId
        createdByEngineId
        createdByResellerId
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.query(query, { engineId });
  return result.getMerchantsByEngine || [];
};

export const getMerchantsByReseller = async (resellerId) => {
  const query = `
    query GetMerchantsByReseller($resellerId: ID!) {
      getMerchantsByReseller(resellerId: $resellerId) {
        id
        code
        email
        name
        status
        createdByResellerId
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.query(query, { resellerId });

  return result.getMerchantsByReseller || [];
};
// ==================== Settlement Operations ====================

export const getAllSettlements = async () => {
  const query = `
    query GetAllSettlements {
      getAllSettlements {
        id
        settlement_id
        merchant_id
        merchant_code
        period_start
        period_end
        total_deposits
        total_withdrawals
        total_fees
        net_amount
        status
        currency
        processed_at
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.query(query);
  return result.getAllSettlements || [];
};

export const createSettlement = async (input) => {
  const mutation = `
    mutation CreateSettlement($input: CreateSettlementInput!) {
      createSettlement(input: $input) {
        id
        settlement_id
        merchant_id
        merchant_code
        period_start
        period_end
        total_deposits
        total_withdrawals
        total_fees
        net_amount
        status
        currency
        processed_at
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.mutate(mutation, { input });
  return result.createSettlement;
};

export const updateSettlementStatus = async (id, input) => {
  const mutation = `
    mutation UpdateSettlementStatus($id: String!, $input: UpdateSettlementStatusInput!) {
      updateSettlementStatus(id: $id, input: $input) {
        id
        settlement_id
        status
        processed_at
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.mutate(mutation, { id, input });
  return result.updateSettlementStatus;
};

// ==================== Dashboard live stats========================

export const getDashboardLiveMetrics = async (startDate, endDate) => {
  const query = `
    query AdminPanelDashboardStats($startDate: String, $endDate: String) {
      adminPanelDashboardStats(startDate: $startDate, endDate: $endDate) {
        currentBalance
        allTimeDeposits
        allTimeWithdrawals
        activeMerchants
        successfulTxCount
        pendingTxCount
        declinedTxCount
      }
    }
  `;

  const result = await graphqlClient.query(query, { startDate, endDate });

  return (
    result?.adminPanelDashboardStats ?? {
      currentBalance: "0.00",
      allTimeDeposits: "0.00",
      allTimeWithdrawals: "0.00",
      activeMerchants: 0,
      successfulTxCount: 0,
      pendingTxCount: 0,
      declinedTxCount: 0,
    }
  );
};

// ==================== Admin Engine Stats (multi-merchant) ========================

export const getAdminEngineStats = async (merchantIds) => {
  const query = `
    query AdminEngineStats($merchantIds: [String!]!) {
      adminEngineStats(merchantIds: $merchantIds) {
        totalVolume
        todayVolume
        successRate
        totalTransactions
        successTransactions
        declinedTransactions
      }
    }
  `;

  const result = await graphqlClient.query(query, { merchantIds });

  return (
    result?.adminEngineStats ?? {
      totalVolume: "0.00",
      todayVolume: "0.00",
      successRate: "0.00",
      totalTransactions: 0,
      successTransactions: 0,
      declinedTransactions: 0,
    }
  );
};

// ==================== Admin Reseller Stats (multi-merchant) ========================

export const getAdminResellerStats = async (merchantIds) => {
  const query = `
    query AdminResellerStats($merchantIds: [String!]!) {
      adminResellerStats(merchantIds: $merchantIds) {
        totalVolume
        todayVolume
        successRate
        totalTransactions
        successTransactions
        declinedTransactions
      }
    }
  `;

  const result = await graphqlClient.query(query, { merchantIds });

  return (
    result?.adminResellerStats ?? {
      totalVolume: "0.00",
      todayVolume: "0.00",
      successRate: "0.00",
      totalTransactions: 0,
      successTransactions: 0,
      declinedTransactions: 0,
      activeMerchants: 0,
    }
  );
};

// ==================== Merchant Dashboard live stats========================

export const getMerchantDashboardLiveMetrics = async (merchantId) => {
  const query = `
    query MerchantDashboardLiveMetrics($merchantId: String!) {
      merchantDashboardLiveMetrics(merchantId: $merchantId) {
        totalWithdrawals
        totalDeposits
        totalVolume
        todayVolume
        successRate
      }
    }
  `;

  const result = await graphqlClient.query(query, { merchantId });

  return (
    result?.merchantDashboardLiveMetrics ?? {
      totalWithdrawals: 0,
      totalDeposits: 0,
      totalVolume: 0,
      todayVolume: 0,
      successRate: 0,
    }
  );
};

//=================== Get merchant by id =======================

export const getMerchantById = async (id) => {
  const query = `
    query GetMerchantById($id: String!) {
      getMerchantById(id: $id) {
        id
        code
        email
        name
        status
        percentageFee
        fee_type
        fee_rate
        settlementPeriod
        depositEnabled
        withdrawalEnabled
        currency
        apiKey
        secretKey
        callbackUrl
        webhookUrl
        createdByAdminId
        createdByEngineId
        createdByResellerId
        parentName
        parentType
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.query(query, { id });
  return result.getMerchantById;
};

export const getMyCreatedMerchants = async () => {
  const query = `
    query GetMyCreatedMerchants {
      getMyCreatedMerchants {
        id
        code
        email
        name
        status
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.query(query);
  return result.getMyCreatedMerchants;
};

export const createMerchant = async (input) => {
  const mutation = `
    mutation CreateMerchant($input: CreateMerchantInput!) {
      createMerchant(input: $input) {
        id
        code
        email
        name
        status
        fee_type
        fee_rate
        createdAt
        updatedAt
        createdByAdminId
        createdByEngineId
        createdByResellerId
      }
    }
  `;

  const result = await graphqlClient.mutate(mutation, { input });
  return result.createMerchant;
};

export const updateMerchant = async (id, input) => {
  const mutation = `
    mutation UpdateMerchant($id: String!, $input: UpdateMerchantInput!) {
      updateMerchant(id: $id, input: $input) {
        id
        code
        email
        name
        status
        fee_type
        fee_rate
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.mutate(mutation, { id, input });
  return result.updateMerchant;
};

export const updateMerchantIpStatus = async (merchantId, ip, status) => {
  const mutation = `
    mutation UpdateMerchantIpStatus($merchantId: String!, $ip: String!, $status: String!) {
      updateMerchantIpStatus(merchantId: $merchantId, ip: $ip, status: $status) {
        id
        ipWhitelistEntries {
          ip
          statusIp
        }
      }
    }
  `;

  const result = await graphqlClient.mutate(mutation, {
    merchantId,
    ip,
    status,
  });
  return result.updateMerchantIpStatus;
};

export const deleteMerchant = async (id) => {
  const mutation = `
    mutation DeleteMerchant($id: String!) {
      deleteMerchant(id: $id)
    }
  `;

  const result = await graphqlClient.mutate(mutation, { id });
  return result.deleteMerchant;
};

export const changeMerchantPassword = async (id, newPassword) => {
  const mutation = `
    mutation ChangeMerchantPassword($input: ChangePasswordInput!) {
      changeMerchantPassword(input: $input)
    }
  `;

  const result = await graphqlClient.mutate(mutation, {
    input: { id, newPassword },
  });
  return result.changeMerchantPassword;
};

// ==================== Dashboard Stats ====================

export const getAdminDashboardStats = async () => {
  const query = `
    query GetAdminDashboardStats {
      getAdminDashboardStats {
        totalEngines
        totalResellers
        totalMerchants
        myCreatedEngines
        myCreatedResellers
        myCreatedMerchants
      }
    }
  `;

  const result = await graphqlClient.query(query);
  return result.getAdminDashboardStats;
};

export const getGlobalDashboardStats = async () => {
  const query = `
    query GetGlobalDashboardStats {
      getGlobalDashboardStats {
        totalEngines
        totalResellers
        totalMerchants
        activeEngines
        activeResellers
        activeMerchants
        inactiveEngines
        inactiveResellers
        inactiveMerchants
      }
    }
  `;

  const result = await graphqlClient.query(query);
  return result.getGlobalDashboardStats;
};

// ==================== Admin Operations ====================

export const getAllAdmins = async () => {
  const query = `
    query GetAllAdmins {
      getAllAdmins {
        id
        email
        name
        status
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.query(query);
  return result.getAllAdmins;
};

export const createAdmin = async (input) => {
  const mutation = `
    mutation CreateAdmin($input: CreateAdminInput!) {
      createAdmin(input: $input) {
        id
        email
        name
        status
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.mutate(mutation, { input });
  return result.createAdmin;
};

// ==================== Staff Operations ====================

export const getMyStaffUsers = async () => {
  const query = `
    query GetMyStaffUsers {
      getMyStaffUsers {
        id
        email
        name
        status
        parentType
        parentId
        roles {
          id
          name
          description
          panelType
          permissions
          isSystem
        }
        permissions
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.query(query);
  return result.getMyStaffUsers;
};

export const getStaffUserById = async (id) => {
  const query = `
    query GetStaffUserById($id: String!) {
      getStaffUserById(id: $id) {
        id
        email
        name
        status
        parentType
        parentId
        roles {
          id
          name
          description
          panelType
          permissions
          isSystem
        }
        permissions
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.query(query, { id });
  return result.getStaffUserById;
};

export const getAvailableRoles = async () => {
  const query = `
    query GetAvailableRoles {
      getAvailableRoles {
        id
        name
        description
        panelType
        permissions
        isSystem
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.query(query);
  return result.getAvailableRoles;
};

export const createStaffUser = async (input) => {
  const mutation = `
    mutation CreateStaffUser($input: CreateStaffUserInput!) {
      createStaffUser(input: $input) {
        id
        email
        name
        status
        parentType
        parentId
        roles {
          id
          name
          description
        }
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.mutate(mutation, { input });
  return result.createStaffUser;
};

export const updateStaffUser = async (id, input) => {
  const mutation = `
    mutation UpdateStaffUser($id: String!, $input: UpdateStaffUserInput!) {
      updateStaffUser(id: $id, input: $input) {
        id
        email
        name
        status
        parentType
        parentId
        roles {
          id
          name
          description
        }
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.mutate(mutation, { id, input });
  return result.updateStaffUser;
};

export const deleteStaffUser = async (id) => {
  const mutation = `
    mutation DeleteStaffUser($id: String!) {
      deleteStaffUser(id: $id)
    }
  `;

  const result = await graphqlClient.mutate(mutation, { id });
  return result.deleteStaffUser;
};

export const assignRoleToStaff = async (input) => {
  const mutation = `
    mutation AssignRoleToStaff($input: AssignRoleInput!) {
      assignRoleToStaff(input: $input) {
        id
        email
        name
        roles {
          id
          name
          description
        }
      }
    }
  `;

  const result = await graphqlClient.mutate(mutation, { input });
  return result.assignRoleToStaff;
};

export const removeRoleFromStaff = async (input) => {
  const mutation = `
    mutation RemoveRoleFromStaff($input: AssignRoleInput!) {
      removeRoleFromStaff(input: $input) {
        id
        email
        name
        roles {
          id
          name
          description
        }
      }
    }
  `;

  const result = await graphqlClient.mutate(mutation, { input });
  return result.removeRoleFromStaff;
};

export const createStaffRole = async (input) => {
  const mutation = `
    mutation CreateStaffRole($input: CreateStaffRoleInput!) {
      createStaffRole(input: $input) {
        id
        name
        description
        panelType
        permissions
        isSystem
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.mutate(mutation, { input });
  return result.createStaffRole;
};

export const updateStaffRole = async (id, input) => {
  const mutation = `
    mutation UpdateStaffRole($id: String!, $input: UpdateStaffRoleInput!) {
      updateStaffRole(id: $id, input: $input) {
        id
        name
        description
        panelType
        permissions
        isSystem
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.mutate(mutation, { id, input });
  return result.updateStaffRole;
};

export const deleteStaffRole = async (id) => {
  const mutation = `
    mutation DeleteStaffRole($id: String!) {
      deleteStaffRole(id: $id)
    }
  `;

  const result = await graphqlClient.mutate(mutation, { id });
  return result.deleteStaffRole;
};

// ==================== Webhook Logs ====================

export const getWebhookLogs = async (limit) => {
  const query = `
    query GetWebhookLogs($limit: Int) {
      getWebhookLogs(limit: $limit) {
        id
        qrCodeId
        paymentId
        merchantId
        merchantName
        event
        url
        httpStatus
        responseBody
        errorMessage
        requestPayload
        createdAt
      }
    }
  `;
  const result = await graphqlClient.query(
    query,
    limit != null ? { limit } : {},
  );
  return result.getWebhookLogs || [];
};
